/**
 * Decoded audio for WebAudio playback, keyed per media input (URL or Blob, see `inputKey`).
 * Loads are de-duplicated; memory is bounded by an LRU budget (decoded PCM is large: ~23 MB per
 * stereo minute at 48 kHz), never evicting pinned inputs. Inputs without an audio track are
 * remembered as silent and resolve to `null`.
 */
import type { MediaInput } from '../types.ts';
import { inputKey } from '../media/input.ts';
import { decodeAudio, NoAudioError } from './decode-audio.ts';

const DEFAULT_BUDGET_BYTES = 1.5 * 1024 ** 3;

interface Entry {
  buffer: AudioBuffer;
  bytes: number;
}

export type AudioDecoder = (input: MediaInput, fallbackDecoder: BaseAudioContext) => Promise<AudioBuffer>;

export class AudioBufferCache {
  private entries = new Map<string, Entry>();
  private pending = new Map<string, Promise<AudioBuffer | null>>();
  private failed = new Map<string, unknown>();
  private silent = new Set<string>();
  private pinned = new Set<string>();
  private bytes = 0;

  constructor(
    private readonly decoder: BaseAudioContext,
    private readonly budgetBytes = DEFAULT_BUDGET_BYTES,
    /** Called with the input's key after a buffer was loaded. */
    private readonly onLoad?: (key: string) => void,
    private readonly decode: AudioDecoder = decodeAudio,
  ) {}

  get(input: MediaInput): AudioBuffer | undefined {
    const key = inputKey(input);
    const e = this.entries.get(key);
    if (!e) return undefined;
    this.entries.delete(key);
    this.entries.set(key, e);
    return e.buffer;
  }

  hasFailed(input: MediaInput): boolean {
    return this.failed.has(inputKey(input));
  }

  /** True once the input is known to have no audio track. */
  isSilent(input: MediaInput): boolean {
    return this.silent.has(inputKey(input));
  }

  /** Inputs that must not be evicted (e.g. used by the current schedule). */
  pin(inputs: Iterable<MediaInput>): void {
    this.pinned = new Set([...inputs].map(inputKey));
  }

  /** Loads (or returns the cached) buffer; `null` if the input has no audio. */
  load(input: MediaInput, decoder: BaseAudioContext = this.decoder): Promise<AudioBuffer | null> {
    const key = inputKey(input);
    const hit = this.get(input);
    if (hit) return Promise.resolve(hit);
    if (this.silent.has(key)) return Promise.resolve(null);
    const inflight = this.pending.get(key);
    if (inflight) return inflight;
    const p = (async () => {
      try {
        const buffer = await this.decode(input, decoder);
        const bytes = buffer.length * buffer.numberOfChannels * 4;
        this.entries.set(key, { buffer, bytes });
        this.bytes += bytes;
        this.failed.delete(key);
        this.evict();
        this.onLoad?.(key);
        return buffer;
      } catch (err) {
        if (err instanceof NoAudioError) {
          this.silent.add(key);
          return null;
        }
        this.failed.set(key, err);
        throw err;
      } finally {
        this.pending.delete(key);
      }
    })();
    this.pending.set(key, p);
    return p;
  }

  /** Starts loading every input not yet loaded/failed/silent; resolves when all settled. */
  async prefetch(inputs: Iterable<MediaInput>): Promise<void> {
    const byKey = new Map<string, MediaInput>();
    for (const i of inputs) byKey.set(inputKey(i), i);
    const loads = [...byKey]
      .filter(([k]) => !this.entries.has(k) && !this.failed.has(k) && !this.silent.has(k))
      .map(([, i]) => this.load(i).catch(() => undefined));
    await Promise.all(loads);
  }

  forget(input: MediaInput): void {
    const key = inputKey(input);
    const e = this.entries.get(key);
    if (e) {
      this.bytes -= e.bytes;
      this.entries.delete(key);
    }
    this.failed.delete(key);
    this.silent.delete(key);
  }

  clearFailures(): void {
    this.failed.clear();
  }

  get memoryBytes(): number {
    return this.bytes;
  }

  private evict(): void {
    for (const [key, e] of this.entries) {
      if (this.bytes <= this.budgetBytes) break;
      if (this.pinned.has(key)) continue;
      this.entries.delete(key);
      this.bytes -= e.bytes;
    }
  }
}
